import { uiText } from '../../i18n/uiText';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckSquare, Download, FileSpreadsheet, FileText, Search, X } from 'lucide-react';
import Dialog from '../common/Dialog';
import { reportsApi } from '../../api';
import { downloadAuditReport, reportExportError } from '../../utils/reportExport';
import '../../styles/audit-report.css';
import ReportPresets from './ReportPresets';
import ReportPdfPreview from './ReportPdfPreview';

const FORMATS = [
  { value: 'pdf', label: 'PDF', hint: uiText("Готовый документ для отправки"), icon: FileText },
  { value: 'docx', label: 'Word', hint: uiText("Редактируемый документ .docx"), icon: FileText },
  { value: 'xlsx', label: 'Excel', hint: uiText("Таблица с ответами .xlsx"), icon: FileSpreadsheet },
];

function SectionCheckbox({ title, questions, selected, disabled, onChange }) {
  const ref = useRef(null);
  const count = questions.filter(question => selected.has(question.key)).length;
  useEffect(() => { if (ref.current) ref.current.indeterminate = count > 0 && count < questions.length; }, [count, questions.length]);
  return (
    <label className="audit-report-section-heading">
      <input ref={ref} type="checkbox" className="ap-check" checked={count === questions.length} disabled={disabled}
        aria-label={uiText("Включить раздел: {v0}", {v0: (title)})} onChange={event => onChange(questions, event.target.checked)} />
      <span>{title}</span><small>{count} / {questions.length}</small>
    </label>
  );
}

export default function AuditReportExportModal({ auditId, auditTitle, onClose, blockedReason = '' }) {
  const [sections, setSections] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [format, setFormat] = useState('pdf');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const [downloaded, setDownloaded] = useState(false);
  const requestRef = useRef(false);
  const exportController = useRef(null);
  const alive = useRef(true);
  const feedbackRef = useRef(null);
  const [catalogMeta,setCatalogMeta] = useState({images:[],unanswered_keys:[],template_id:null});
  const [variants,setVariants] = useState({});
  const [preview,setPreview] = useState(null);
  const previewRef = useRef(null);
  const previewHeadingRef = useRef(null);
  const clearPreview = () => { if(previewRef.current)URL.revokeObjectURL(previewRef.current);previewRef.current=null;setPreview(null); };

  useEffect(() => {
    if (!preview) return;
    previewHeadingRef.current?.focus({ preventScroll: true });
    previewHeadingRef.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' });
  }, [preview]);

  useEffect(() => {
    if (error || loadError) feedbackRef.current?.focus({ preventScroll: true });
  }, [error, loadError]);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; exportController.current?.abort(); if(previewRef.current)URL.revokeObjectURL(previewRef.current); };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true); setLoadError('');
    reportsApi.questions(auditId, { signal: controller.signal }).then(response => {
      if (!active) return;
      const items = (response.data?.sections || []).filter(section => section.questions?.length);
      setSections(items);
      setSelected(new Set(items.flatMap(section => section.questions.map(question => question.key))));
      setCatalogMeta({images:response.data?.images||[],unanswered_keys:response.data?.unanswered_keys||[],template_id:response.data?.template_id??null});
      setVariants({});clearPreview();
    }).catch(async requestError => {
      const message = await reportExportError(requestError);
      if (active) setLoadError(message);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [auditId, retry]);

  const questions = useMemo(() => sections.flatMap(section => section.questions), [sections]);
  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('ru-RU');
    return sections.map(section => ({ ...section, questions: section.questions.filter(question =>
      !needle || `${section.title} ${question.number} ${question.text}`.toLocaleLowerCase('ru-RU').includes(needle)),
    })).filter(section => section.questions.length);
  }, [sections, search]);
  const visibleQuestions = filtered.flatMap(section => section.questions);
  const selectedCount = questions.filter(question => selected.has(question.key)).length;
  const formatLabel = FORMATS.find(item => item.value === format).label;
  const annotationIds = catalogMeta.images.filter(file=>selected.has(file.question_key)&&variants[file.id]).map(file=>Number(variants[file.id]));
  const missingCount = catalogMeta.unanswered_keys.filter(key=>selected.has(key)).length;
  const requestData = outputFormat => ({format:outputFormat,question_keys:questions.filter(question=>selected.has(question.key)).map(question=>question.key),...(outputFormat!=='xlsx'&&annotationIds.length?{annotation_ids:annotationIds}:{})});
  const selectionKey = JSON.stringify(requestData('pdf'));
  const previewCurrent = preview?.key===selectionKey;

  const choose = (items, checked) => {
    if (busy) return;
    setSelected(previous => {
      const next = new Set(previous);
      items.forEach(question => checked ? next.add(question.key) : next.delete(question.key));
      return next;
    });
    setDownloaded(false);
  };

  const download = async () => {
    if (requestRef.current || loading || loadError || !selectedCount || blockedReason) return;
    requestRef.current = true; setBusy(true); setError(''); setDownloaded(false);
    exportController.current = new AbortController();
    try {
      // Export exactly the catalogue snapshot the user saw. A question added
      // concurrently by another editor must not silently enter this document.
      const response = format==='pdf'&&previewCurrent ? preview.response : await reportsApi.export(auditId, requestData(format), { signal: exportController.current.signal });
      if (!alive.current) return;
      downloadAuditReport(response, auditId, format);
      setDownloaded(true);
    } catch (requestError) {
      const message = await reportExportError(requestError);
      if (alive.current) setError(message);
    } finally {
      requestRef.current = false;
      if (alive.current) setBusy(false);
    }
  };

  const buildPreview = async () => {
    if(requestRef.current||loading||loadError||!selectedCount||blockedReason)return;
    requestRef.current=true;setBusy(true);setError('');clearPreview();
    exportController.current=new AbortController();
    try{
      const response=await reportsApi.export(auditId,requestData('pdf'),{signal:exportController.current.signal});
      if(!alive.current)return;
      const blob=new Blob([response.data],{type:'application/pdf'});
      const head=await new Promise((resolve,reject)=>{const r=new FileReader();r.onload=()=>resolve(r.result);r.onerror=reject;r.readAsText(blob.slice(0,5));});
      if(head!=='%PDF-')throw new Error(uiText("Сервер не вернул PDF. Повторите формирование отчёта."));
      if(!alive.current)return;
      const url=URL.createObjectURL(blob);previewRef.current=url;setPreview({url,response,key:selectionKey});
    }catch(e){if(alive.current)setError(await reportExportError(e));}
    finally{requestRef.current=false;if(alive.current)setBusy(false);}
  };

  return (
    <Dialog size="lg" className="audit-report-dialog" labelledBy="audit-report-title" describedBy="audit-report-description" onClose={onClose} closeDisabled={busy}>
      <div className="ap-dialog__header">
        <div>
          <h2 className="ap-dialog__title" id="audit-report-title" data-dialog-heading tabIndex={-1}><Download size={20} />{uiText("Отчёт по аудиту")}</h2>
          <p className="ap-dialog__description audit-report-audit-title">{auditTitle}</p>
        </div>
        <button type="button" className="ap-dialog__close" aria-label={uiText("Закрыть")} onClick={onClose} disabled={busy}><X size={18} /></button>
      </div>
      <div className="ap-dialog__body audit-report-body">
        <p className="audit-report-help" id="audit-report-description">{uiText("Выберите формат и вопросы для этого файла. Снятие галочки не удаляет вопрос или ответ из аудита. Изображения в PDF и Word автоматически сжимаются.")}</p>
        <fieldset className="audit-report-formats" disabled={busy}>
          <legend>{uiText("Формат документа")}</legend>
          <div className="audit-report-format-grid">
            {FORMATS.map(({ value, label, hint, icon: Icon }) => (
              <label key={value} className={`audit-report-format${format === value ? ' is-selected' : ''}`}>
                <input type="radio" className="ap-check" name="audit-report-format" value={value} checked={format === value} onChange={() => { setFormat(value); setDownloaded(false); }} aria-label={label} />
                <Icon size={20} aria-hidden="true" /><span><strong>{label}</strong><small>{hint}</small></span>
              </label>
            ))}
          </div>
        </fieldset>
        {loading ? <div className="audit-report-empty" role="status">{uiText("Загружаем вопросы чек-листа…")}</div> : null}
        {!loading && !loadError && !questions.length ? <div className="audit-report-empty">{uiText("В чек-листе пока нет вопросов для отчёта.")}</div> : null}
        {!loading && !loadError && questions.length > 0 ? <>
          <ReportPresets auditId={auditId} templateId={catalogMeta.template_id} questions={questions} selected={selected} format={format} disabled={busy} onApply={(keys,nextFormat)=>{setSelected(keys);setFormat(nextFormat);setDownloaded(false);}} />
          {missingCount>0&&<p className="audit-report-notice">{uiText("В выбранных вопросах без сохранённого ответа и вложений: ")}{missingCount}{uiText(". Это предупреждение о составе документа, а не новое условие завершения аудита.")}</p>}
          <div className="audit-report-selection-tools">
            <div className="audit-report-selection-title"><CheckSquare size={17} aria-hidden="true" /><strong>{uiText("Вопросы в отчёте")}</strong><span role="status" aria-live="polite">{uiText("Выбрано ")}{selectedCount}{uiText(" из ")}{questions.length}</span></div>
            <label className="audit-report-search"><Search size={17} aria-hidden="true" /><input type="search" className="ap-control" aria-label={uiText("Поиск вопросов для отчёта")} placeholder={uiText("Поиск по вопросу, номеру или разделу")} value={search} onChange={event => setSearch(event.target.value)} disabled={busy} /></label>
            <div className="audit-report-selection-actions">
              <button type="button" className="ap-button ap-button--secondary ap-button--sm" onClick={() => choose(questions, true)} disabled={busy || selectedCount === questions.length}>{uiText("Выбрать все")}</button>
              <button type="button" className="ap-button ap-button--secondary ap-button--sm" onClick={() => choose(questions, false)} disabled={busy || !selectedCount}>{uiText("Снять выбор")}</button>
              {search.trim() ? <span>{uiText("Найдено: ")}{visibleQuestions.length}{uiText(". Поиск не меняет выбор.")}</span> : null}
            </div>
          </div>
          <div className="audit-report-sections">
            {!filtered.length ? <div className="audit-report-empty">{uiText("Ничего не найдено. Измените поиск — ваш выбор сохранён.")}</div> : null}
            {filtered.map((section, index) => (
              <section className="audit-report-section" key={`${section.title}-${index}`}>
                <SectionCheckbox title={section.title} questions={section.questions} selected={selected} disabled={busy} onChange={choose} />
                {section.questions.map(question => <label className={`audit-report-question${selected.has(question.key) ? '' : ' is-excluded'}`} key={question.key}>
                  <input type="checkbox" className="ap-check" checked={selected.has(question.key)} disabled={busy} onChange={event => choose([question], event.target.checked)} aria-label={uiText("Включить вопрос {v0}: {v1}", {v0: (question.number), v1: (question.text)})} />
                  <span className="audit-report-question-number">{question.number}.</span><span>{question.text}</span>
                </label>)}
              </section>
            ))}
          </div>
          {format!=='xlsx'&&catalogMeta.images.some(file=>selected.has(file.question_key)&&file.annotations?.length)&&<details className="audit-report-variants"><summary>{uiText("Оригиналы и разметка фотографий")}</summary><p className="audit-report-help">{uiText("По умолчанию включаются оригиналы. Выберите сохранённую разметку только для нужных изображений; исходные файлы не меняются.")}</p>{catalogMeta.images.filter(file=>selected.has(file.question_key)&&file.annotations?.length).map(file=><label key={file.id}>{file.name}<select aria-label={file.name} className="ap-control" value={variants[file.id]||''} disabled={busy} onChange={e=>{setVariants(v=>({...v,[file.id]:e.target.value}));setDownloaded(false);}}><option value="">{uiText("Оригинал")}</option>{file.annotations.map(a=><option key={a.id} value={a.id}>{uiText("Разметка №")}{a.id} · {new Date(a.created_at).toLocaleString('ru')}</option>)}</select></label>)}</details>}
          {preview&&<section className="audit-report-preview"><h3 ref={previewHeadingRef} tabIndex={-1}>{uiText("Предпросмотр PDF")}</h3>{previewCurrent?<><p className="audit-report-help">{uiText("При скачивании PDF будет передан именно этот сформированный файл. Для Word и Excel расположение страниц может отличаться.")}</p><ReportPdfPreview url={preview.url}/><a className="ap-button ap-button--secondary" href={preview.url} target="_blank" rel="noreferrer">{uiText("Открыть PDF отдельно")}</a></>:<p className="audit-report-notice">{uiText("Состав изменён. Сформируйте новый предпросмотр; предыдущий документ больше не соответствует выбору.")}</p>}</section>}
        </> : null}
      </div>
      {error || loadError ? <div className="audit-report-feedback is-error" role="alert" ref={feedbackRef} tabIndex={-1}>
        <span>{error || loadError}</span>
        {loadError ? <button type="button" className="ap-button ap-button--secondary ap-button--sm" onClick={() => setRetry(value => value + 1)}>{uiText("Повторить загрузку")}</button> : null}
      </div> : blockedReason ? <div className="audit-report-feedback" role="note">{blockedReason}</div>
        : downloaded ? <div className="audit-report-feedback" role="status">{uiText("Файл подготовлен и передан браузеру. Проверьте папку загрузок.")}</div> : null}
      <div className="ap-dialog__footer audit-report-footer">
        <span aria-live="polite">{busy ? uiText("Формируем документ. Большой отчёт может занять несколько минут…") : !selectedCount && !loading && !loadError ? uiText("Выберите хотя бы один вопрос.") : uiText("Настройки действуют только для этого скачивания.")}</span>
        <button type="button" className="ap-button ap-button--secondary" onClick={onClose} disabled={busy}>{uiText("Закрыть")}</button>
        <button type="button" className="ap-button ap-button--secondary" onClick={buildPreview} disabled={busy||loading||!!loadError||!selectedCount||!!blockedReason}>{busy?uiText("Формируем…"):previewCurrent?uiText("Обновить предпросмотр"):uiText("Предпросмотр PDF")}</button>
        <button type="button" className="ap-button ap-button--primary" onClick={download} disabled={busy || loading || !!loadError || !selectedCount || !!blockedReason}><Download size={16} aria-hidden="true" />{busy ? uiText("Подготавливаем…") : uiText("Скачать {v0}", {v0: (formatLabel)})}</button>
      </div>
    </Dialog>
  );
}
